import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from "@angular/core";
import { SHARED_ENVIRONMENT } from '@k12/environment';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { GuardianChangeTaskRequest,
         HouseholdRecordVm,
         HouseholdUpdatePayload,
         SchoolTypeResponse,
         HouseholdStudent,
         HouseHoldDetails,
         DisabilityType,
         studentNote,
         HouseholdGuardianOption,
         notification,
         notifications,
         School,
         SchoolResponse,
         StudentSearchResult,
         StartHouseholdStudentWithdrawalRequest,
         ApiResponse,
         HouseholdIncomeVerificationDetails,
         IncomeVerificationDetails
        } from '@k12/models';
import { GuardianChangeFormContextResponse } from 'projects/libs/models/src/models/guardian-change-form-context.model';

export interface SaveResponse {
  message?: string;
  success?: boolean;
  [key: string]: unknown;
}

export enum UserType {
  Admin = 'Admin',
  User = 'User'
}

@Injectable({
  providedIn: 'root'
})
export class HouseholdService {
  public notifications$ = new BehaviorSubject<notifications[]>(notification);

  private readonly sharedEnvironment = inject(SHARED_ENVIRONMENT);
  private readonly apiUrl = this.sharedEnvironment.adminApiUrl;
  private readonly http = inject(HttpClient);
  private readonly notificationDataKey = 'householdNotificationData';

  public getHouseholdDetails(academicYearId: number | null, householdId?: string): Observable<HouseHoldDetails> {
    const params: Record<string, string | number> = {};
    if (academicYearId !== null) {
      params['academicYearId'] = academicYearId;
    }
    // The API binds the household's ID to its programPartnerId query parameter.
    if (householdId) {
      params['programPartnerId'] = householdId;
    }
    return this.http.get<HouseHoldDetails>(`${this.apiUrl}/household/details`, { params });
  }

  public saveHouseholdInfo(payload: HouseholdRecordVm): Observable<HouseholdRecordVm> {
    return this.http.put<HouseholdRecordVm>(`${this.apiUrl}/household/householdInfo`, payload);
  }

  public saveStudentInfo(payload: HouseholdStudent): Observable<SaveResponse> {
    return this.http.put<SaveResponse>(`${this.apiUrl}/household/studentInfo`, payload);
  }

  public getAllHouseholdStudents(): Observable<HouseholdStudent[]> {
    return this.http.get<HouseholdStudent[]>(`${this.apiUrl}/household/students`);
  }

  public getHouseholdStudentDetails(id: string | null, academicYearId: number | null = null): Observable<HouseholdStudent> {
    const params: Record<string, string | number> = {};
    if (academicYearId !== null) {
      params['academicYearId'] = academicYearId;
    }

    return this.http.get<HouseholdStudent>(`${this.apiUrl}/household/student/${id}`, { params });
  }

  public getStudentNotesByStudentId(studentId: string | null): Observable<studentNote[]> {
    return this.http.get<studentNote[]>(`${this.apiUrl}/household/student/${studentId}/notes`);
  }

  public addStudentNote(studentId: string, payload: { text: string, studentId: string | null }): Observable<studentNote> {
    return this.http.post<studentNote>(`${this.apiUrl}/household/student/${studentId}/note`, payload);
  }

  public updateStudentNote(studentId: string | null, noteId: string, payload: studentNote): Observable<boolean> {
    return this.http.put<boolean>(`${this.apiUrl}/household/student/${studentId}/note/${noteId}`, payload);
  }

  public deleteStudentNote(studentId: string | null, noteId: string): Observable<boolean> {
    return this.http.delete<boolean>(`${this.apiUrl}/household/student/${studentId}/note/${noteId}`);
  }

  public getAllStudentsByHousehold(householdId: string): Observable<HouseholdStudent[]> {
    return this.http.get<HouseholdStudent[]>(`${this.apiUrl}/household/students?householdId=${householdId}`);
  }

  public getDisabilityTypes(): Observable<DisabilityType[]> {
    return this.http.get<DisabilityType[]>(`${this.apiUrl}/household/student/disability`);
  }

  public searchGuardianHouseholds(q: string): Observable<HouseholdGuardianOption[]> {
    return this.http.get<HouseholdGuardianOption[]>(`${this.apiUrl}/household/guardianSearch`, { params: { q } });
  }

  public searchStudents(q: string | null, limit?: number): Observable<StudentSearchResult[]> {
    const params: Record<string, string | number> = {};
    if (q) params['q'] = q;
    if (limit !== undefined && limit !== null) params['limit'] = limit;
    return this.http.get<StudentSearchResult[]>(`${this.apiUrl}/household/studentSearch`, { params });
  }

  public loadNotificationData(): void {
    this.notifications$.next(this.loadLocalNotificationData());
  }

  public loadLocalNotificationData(): notifications[] {
    const data = localStorage.getItem(this.notificationDataKey);
    return data ? JSON.parse(data) : [];
  }

  public saveNotificationData(): void {
    localStorage.setItem(this.notificationDataKey, JSON.stringify(this.notifications$.value));
  }

  public initializeNotificationData(): void {
    if (!localStorage.getItem(this.notificationDataKey)) {
      localStorage.setItem(this.notificationDataKey, JSON.stringify(notification));
    }
    this.loadNotificationData();
  }

  public createGuardianChangeTask(studentId: string | undefined): Observable<void>{
    const request: GuardianChangeTaskRequest = {studentId};
     if (!studentId) {
        return throwError(() => new Error('StudentId is required to create a guardian change task.'));
      }

    return this.http.post<void>(
      `${this.apiUrl}/household/guardian-change-task`,
      request
    )
  }

  public updateNotificationData(id: number, newNotification: notifications): void {
    const updatedNotifications = this.notifications$.value.map((notification: notifications) =>
      notification.id === id ? newNotification : notification
    );
    this.notifications$.next(updatedNotifications);
    this.saveNotificationData();
  }

  public getSchools(): Observable<School[]> {
    return this.http.get(`${this.apiUrl}/partners/schools`) as Observable<School[]>;
  }

  public getSchoolsForDropdown(): Observable<SchoolResponse> {
    return this.http.get(`${this.apiUrl}/schools/GetSchoolsForDropDown`) as Observable<SchoolResponse>;
  }

  public getSchoolTypesForDropdown(search: string, activeOnly: boolean = false): Observable<SchoolTypeResponse> {
    const params = {
      search: search,
      activeOnly: activeOnly
    };
    return this.http.get<SchoolTypeResponse>(`${this.apiUrl}/schools/GetSchoolTypesForDropDown`, {
      params
    });
  }

    startStudentWithdrawal(request: StartHouseholdStudentWithdrawalRequest): Observable<ApiResponse> {
      return this.http.post<ApiResponse>(`${this.apiUrl}/household/withdrawal`, request);
    }
  

  updateHousehold(payload: HouseholdUpdatePayload): Observable<void> {
    return this.http.put<void>(`${this.apiUrl}/household/householdInfo`, payload);
  }

  public getGuardianChangeFormContext(studentId: string) : Observable<GuardianChangeFormContextResponse>{
    return this.http.get<GuardianChangeFormContextResponse>(`${this.apiUrl}/household/guardian-change-form-context/${studentId}`)
  }

  getIncomeVerificationDetails(): Observable<HouseholdIncomeVerificationDetails> {
    return this.http.get<HouseholdIncomeVerificationDetails>(`${this.apiUrl}/household/income-verificationData`);
  }

  selectRandomIVforOSStudents(percentage: number, academicYearId: number){
    return this.http.put(`${this.apiUrl}/household/income-verification/random-os`, 
      null,
      {
        params: {
          percentage: percentage, 
          academicYearId: academicYearId
        }
      }
    )
  }
  getIncomeVerificationDetailsByHousehold(householdId: string): Observable<IncomeVerificationDetails> {
    return this.http.get<IncomeVerificationDetails>(`${this.apiUrl}/household/income-verificationData-householdId/${householdId}`)
  }
}